import { useState, useEffect } from "react";
import { useAuth } from "../Auth/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import PageWrapper from "../Components/Animations/PageWrapper";
import toast from "react-hot-toast";
import AppSettings from "../appsettings";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPopup, setShowPopup] = useState(AppSettings.Maintainance_Mode);
  const [isHovered, setIsHovered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  try {
    const result = await login(email, password);
    if (result) {
      setLoginSuccess(true);
    }
  } catch (errMsg) {
    toast.error(errMsg);
  } finally {
    setLoading(false);
  }
};


  return (
    <>
      <PageWrapper>
        <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-gray-900 via-purple-900 to-violet-800">
          {/* Floating particles background */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-white/10"
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: Math.random() * window.innerHeight,
                  width: Math.random() * 10 + 5,
                  height: Math.random() * 10 + 5,
                }}
                animate={{
                  x: [null, Math.random() * 50 - 25],
                  y: [null, Math.random() * 50 - 25],
                }}
                transition={{
                  duration: Math.random() * 10 + 10,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>

          {/* Under Construction Popup */}
          <AnimatePresence>
            {showPopup && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
              >
                <motion.div
                  className="bg-gradient-to-br from-gray-800 to-violet-900/80 backdrop-blur-lg border border-white/10 rounded-2xl p-8 max-w-md w-full shadow-2xl text-center relative overflow-hidden"
                  initial={{ scale: 0.9, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.9, y: 20 }}
                >
                  <div className="absolute -top-20 -right-20 w-40 h-40 bg-violet-600/30 rounded-full blur-xl"></div>
                  <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-indigo-600/30 rounded-full blur-xl"></div>

                  <motion.div
                    animate={{ rotate: isHovered ? 5 : 0 }}
                    className="inline-block mb-4 text-4xl"
                  >
                    🚧
                  </motion.div>
                  <h2 className="text-2xl font-bold text-white mb-3">
                    Under Construction
                  </h2>
                  <p className="text-white/80 mb-6">
                    We're working hard to bring you an amazing experience.
                    The dashboard will be available soon!
                  </p>
                  <motion.button
                    whileHover={{
                      scale: 1.05,
                      backgroundColor: "rgba(255,255,255,0.9)"
                    }}
                    whileTap={{ scale: 0.95 }}
                    onHoverStart={() => setIsHovered(true)}
                    onHoverEnd={() => setIsHovered(false)}
                    onClick={() => setShowPopup(false)}
                    className="px-6 py-2.5 bg-white text-violet-900 font-medium rounded-lg hover:shadow-lg transition-all"
                  >
                    Continue to Login
                  </motion.button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Login Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative z-10 w-full max-w-md px-4"
          >
            <motion.form
              onSubmit={handleSubmit}
              className="relative backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl p-8 shadow-2xl overflow-hidden"
              whileHover={{ boxShadow: "0 10px 25px -5px rgba(196, 181, 253, 0.3)" }}
            >
              {/* Form shine effect */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] animate-rotate bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-20" />
              </div>

              {/* Form content */}
              <div className="relative z-10">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                    </svg>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-center text-white mb-2">
                  Welcome To AdBlastX World
                </h2>
                <p className="text-center text-white/70 mb-8 text-sm">
                  Sign in to access your dashboard
                </p>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1.5">
                      Email Address
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition text-white placeholder-white/40"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                      <div className="absolute right-3 top-3 text-white/40">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1.5">
                      <label className="block text-sm font-medium text-white/80">
                        Password
                      </label>
                      {/* <a href="#" className="text-xs text-violet-300 hover:text-violet-200 transition">
                        Forgot password?
                      </a> */}
                    </div>
                    <div className="relative">
                      <input
                        type="password"
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition text-white placeholder-white/40"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <div className="absolute right-3 top-3 text-white/40">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-3.5 text-white">
                      <svg className="animate-spin h-5 w-5 mx-auto mb-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                      </svg>
                      Logging in...
                    </div>
                  ) : loginSuccess ? (
                    <div className="text-center py-3.5 text-green-400 font-semibold">
                      ✅ Login Successful!
                    </div>
                  ) : (
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02, boxShadow: "0 4px 14px rgba(139, 92, 246, 0.4)" }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-medium text-sm tracking-wide shadow-lg hover:shadow-violet-500/30 transition-all"
                    >
                      Sign In
                    </motion.button>
                  )}

                </div>

                {/* <div className="mt-6 text-center text-sm text-white/50">
                  Don't have an account?{" "}
                  <a href="#" className="text-violet-300 hover:text-white transition font-medium">
                    Sign up
                  </a>
                </div> */}
              </div>
            </motion.form>

            <div className="mt-6 text-center text-xs text-white/40">
              By continuing, you agree to our Terms and Privacy Policy
            </div>
          </motion.div>
        </div>
      </PageWrapper>

      {/* Add this to your global CSS */}
      <style jsx global>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-rotate {
          animation: rotate 10s linear infinite;
        }
      `}</style>
    </>
  );
}